import type { LocalHorizontal } from "./coordinates.ts";
import type { RuntimeWorldTilePayloadV2 } from "./schema/runtimeTile.ts";

export interface StreetLocationConfig {
  readonly acquireRadiusM: number;
  readonly releaseRadiusM: number;
  readonly switchAdvantageM: number;
  readonly sampleMovementM: number;
}

export interface StreetLocation {
  readonly name: string;
  readonly distanceM: number;
}

function distanceToSegmentM(
  point: Readonly<LocalHorizontal>,
  from: Readonly<LocalHorizontal>,
  to: Readonly<LocalHorizontal>,
): number {
  const segmentX = to.x - from.x;
  const segmentZ = to.z - from.z;
  const lengthSquared = segmentX * segmentX + segmentZ * segmentZ;
  if (lengthSquared <= Number.EPSILON) {
    return Math.hypot(point.x - from.x, point.z - from.z);
  }
  const projection = Math.max(
    0,
    Math.min(
      1,
      (
        (point.x - from.x) * segmentX +
        (point.z - from.z) * segmentZ
      ) / lengthSquared,
    ),
  );
  return Math.hypot(
    point.x - (from.x + segmentX * projection),
    point.z - (from.z + segmentZ * projection),
  );
}

function namedStreetCandidates(
  tiles: readonly Readonly<RuntimeWorldTilePayloadV2>[],
  position: Readonly<LocalHorizontal>,
): readonly StreetLocation[] {
  const distanceByName = new Map<string, number>();
  for (const tile of tiles) {
    for (const feature of tile.transportLines) {
      const name = feature.name?.trim();
      if (name === undefined || name.length === 0) {
        continue;
      }
      for (let index = 1; index < feature.points.length; index += 1) {
        const from = feature.points[index - 1];
        const to = feature.points[index];
        if (from === undefined || to === undefined) {
          continue;
        }
        const distanceM = distanceToSegmentM(
          position,
          {
            x: tile.origin.x + from.x,
            z: tile.origin.z + from.z,
          },
          {
            x: tile.origin.x + to.x,
            z: tile.origin.z + to.z,
          },
        );
        distanceByName.set(
          name,
          Math.min(distanceByName.get(name) ?? Number.POSITIVE_INFINITY, distanceM),
        );
      }
    }
  }
  return [...distanceByName]
    .map(([name, distanceM]) => ({ name, distanceM }))
    .sort(
      (left, right) =>
        left.distanceM - right.distanceM ||
        left.name.localeCompare(right.name, "lt-LT"),
    );
}

function validateConfig(config: Readonly<StreetLocationConfig>): void {
  for (const [name, value] of Object.entries(config)) {
    if (!Number.isFinite(value) || value < 0) {
      throw new Error(`${name} must be a finite non-negative number`);
    }
  }
  if (config.releaseRadiusM < config.acquireRadiusM) {
    throw new Error("releaseRadiusM must be at least acquireRadiusM");
  }
}

/**
 * Resolves the nearest official named transport segment, retaining the current
 * street until it is clearly out of range or another name is materially closer.
 */
export function updateStreetLocation(
  tiles: readonly Readonly<RuntimeWorldTilePayloadV2>[],
  position: Readonly<LocalHorizontal>,
  previous: Readonly<StreetLocation> | null,
  config: Readonly<StreetLocationConfig>,
): StreetLocation | null {
  validateConfig(config);
  if (!Number.isFinite(position.x) || !Number.isFinite(position.z)) {
    throw new Error("Street-location coordinates must be finite");
  }
  const candidates = namedStreetCandidates(tiles, position);
  const nearest = candidates[0];
  const current = previous === null
    ? undefined
    : candidates.find((candidate) => candidate.name === previous.name);

  if (current !== undefined && current.distanceM <= config.releaseRadiusM) {
    if (
      nearest === undefined ||
      nearest.name === current.name ||
      current.distanceM - nearest.distanceM < config.switchAdvantageM
    ) {
      return current;
    }
  }
  return nearest !== undefined && nearest.distanceM <= config.acquireRadiusM
    ? nearest
    : null;
}
